/**
 * Object storage — one API over two backends.
 *
 * Demo / Supabase builds keep using Supabase Storage exactly as before.
 * The Azure enterprise build routes every operation through the API tier's
 * /api/storage/object broker, which talks to Azure Blob Storage using the
 * container's managed identity. The browser therefore never holds a storage
 * credential on either backend, and no call site has to know which one it is
 * talking to.
 *
 * Buckets and Blob containers deliberately share names ('project-documents',
 * 'form-attachments', 'skill-evidence'), so a stored path is portable between
 * the two deployments and existing metadata rows need no rewriting.
 */
import { supabase } from './supabase'
import { isEntraAuthEnabled } from './entraAuth'

export type StorageContainer =
  | 'project-documents'
  | 'form-attachments'
  | 'skill-evidence'
  | 'compliance-documents'
  | 'test-evidence'
  | 'test-scripts'
  | 'defect-attachments'
  | 'ticket-attachments'

async function broker<T>(payload: Record<string, unknown>): Promise<T> {
  // The bootstrap fetch interceptor attaches the Entra bearer.
  const res = await fetch('/api/storage/object', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
  const json = (await res.json().catch(() => ({}))) as { error?: string }
  if (!res.ok) throw new Error(json.error || `Storage request failed (${res.status})`)
  return json as T
}

/** Base64 for the broker. Chunked so large files don't blow the call stack. */
async function fileToBase64(file: Blob): Promise<string> {
  const bytes = new Uint8Array(await file.arrayBuffer())
  let binary = ''
  const CHUNK = 0x8000
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK))
  }
  return btoa(binary)
}

/** Upload an object. Throws with a readable message on failure. */
export async function uploadObject(
  container: StorageContainer,
  path: string,
  file: File | Blob,
  opts?: { contentType?: string; upsert?: boolean },
): Promise<void> {
  if (isEntraAuthEnabled()) {
    await broker({
      op: 'upload',
      container,
      path,
      contentType: opts?.contentType ?? (file instanceof File ? file.type : undefined),
      dataBase64: await fileToBase64(file),
    })
    return
  }
  const { error } = await supabase.storage
    .from(container)
    .upload(path, file, { upsert: opts?.upsert ?? false, contentType: opts?.contentType })
  if (error) throw new Error(error.message)
}

/** Remove an object. Missing objects are not an error on either backend. */
export async function removeObject(container: StorageContainer, path: string): Promise<void> {
  if (isEntraAuthEnabled()) {
    await broker({ op: 'delete', container, path })
    return
  }
  await supabase.storage.from(container).remove([path])
}

/** A short-lived read URL for downloading or viewing an object. */
export async function signedUrl(
  container: StorageContainer,
  path: string,
  expiresInSeconds = 3600,
): Promise<string> {
  if (isEntraAuthEnabled()) {
    const r = await broker<{ url: string }>({ op: 'signedUrl', container, path, expiresInSeconds })
    return r.url
  }
  const { data, error } = await supabase.storage.from(container).createSignedUrl(path, expiresInSeconds)
  if (error || !data?.signedUrl) throw new Error(error?.message ?? 'Could not create link')
  return data.signedUrl
}

/**
 * Download an object's bytes (used to assemble zips client-side).
 * On Azure this fetches the signed URL rather than proxying the bytes through
 * the API tier, so large files stream straight from Blob.
 */
export async function downloadObject(container: StorageContainer, path: string): Promise<Blob> {
  if (isEntraAuthEnabled()) {
    const url = await signedUrl(container, path, 600)
    const res = await fetch(url)
    if (!res.ok) throw new Error(`Download failed (${res.status})`)
    return res.blob()
  }
  const { data, error } = await supabase.storage.from(container).download(path)
  if (error || !data) throw new Error(error?.message ?? 'Download failed')
  return data
}

/**
 * A URL for displaying an object.
 *
 * Supabase buckets can be public, so that path returns a stable public URL.
 * Blob has no public access by design, so the Azure path returns a signed URL
 * instead — hence the async signature, which both backends share.
 */
export async function displayUrl(container: StorageContainer, path: string): Promise<string> {
  if (isEntraAuthEnabled()) return signedUrl(container, path, 3600)
  return supabase.storage.from(container).getPublicUrl(path).data.publicUrl
}
